import { apiService } from '../../../components/api/apiService';
import { coercePolicies } from './omniChatConstants';
import { logOmniChat } from './omniChatLogger';
import type { OmniChatAgent, OmniChatAuditEvent, OmniChatFormState, OmniChatIconId } from './omniChatTypes';

export const OMNICHAT_CONNECTION = 'backend_py';
export const OMNICHAT_API_BASE = '/api/v1/omnichat';
export const OMNICHAT_AUDIT_BASE = '/api/v1/reporting/omnichat/audit';

const ICON_IDS: OmniChatIconId[] = [
  'messages',
  'bot',
  'sparkles',
  'headset',
  'lifebuoy',
  'briefcase',
  'book',
  'shield',
];

function serviceErrorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string }; message?: string }; status?: number };
    message?: string;
  };
  if (axiosErr.code === 'ERR_NETWORK' || axiosErr.message === 'Network Error') {
    return 'Cannot reach BackendPYApp. Start it with `python run.py` from BackendPYApp.';
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'OmniChat request failed'
  );
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function asIconId(value: unknown): OmniChatIconId {
  return ICON_IDS.includes(value as OmniChatIconId) ? (value as OmniChatIconId) : 'messages';
}

function asText(raw: Record<string, unknown>, camel: string, snake: string): string {
  const value = raw[camel] ?? raw[snake];
  return value == null ? '' : String(value);
}

function normalizeAgent(raw: Record<string, unknown>): OmniChatAgent {
  return {
    id: String(raw.id || ''),
    title: String(raw.title || ''),
    description: String(raw.description || ''),
    iconId: asIconId(raw.iconId || raw.icon_id),
    instruction: String(raw.instruction || ''),
    gatewayId: String(raw.gatewayId || raw.gateway_id || ''),
    inferenceQuotaId: String(raw.inferenceQuotaId || raw.inference_quota_id || ''),
    inferenceKeyLast4: String(raw.inferenceKeyLast4 || raw.inference_key_last4 || ''),
    connectorIds: asArray<string>(raw.connectorIds || raw.connector_ids).map(String),
    knowledgeBaseIds: asArray<string>(raw.knowledgeBaseIds || raw.knowledge_base_ids).map(String),
    policies: coercePolicies((raw.policies as OmniChatAgent['policies']) || null),
    active: raw.active !== false && raw.active !== 'false',
    createdBy: asText(raw, 'createdBy', 'created_by'),
    createdByName: asText(raw, 'createdByName', 'created_by_name'),
    createdByEmail: asText(raw, 'createdByEmail', 'created_by_email'),
    createdAt: asText(raw, 'createdAt', 'created_at'),
    updatedAt: asText(raw, 'updatedAt', 'updated_at'),
  };
}

function normalizeAudit(raw: Record<string, unknown>): OmniChatAuditEvent {
  return {
    eventId: asText(raw, 'eventId', 'event_id'),
    action: asText(raw, 'action', 'action'),
    outcome: asText(raw, 'outcome', 'outcome') || 'succeeded',
    summary: asText(raw, 'summary', 'summary'),
    actorId: asText(raw, 'actorId', 'actor_id'),
    actorName: asText(raw, 'actorName', 'actor_name'),
    actorEmail: asText(raw, 'actorEmail', 'actor_email'),
    occurredAt: asText(raw, 'occurredAt', 'occurred_at'),
    agentId: asText(raw, 'agentId', 'agent_id'),
    agentName: asText(raw, 'agentName', 'agent_name'),
  };
}

function formPayload(form: OmniChatFormState) {
  return {
    title: form.title.trim(),
    description: form.description.trim(),
    iconId: form.iconId,
    instruction: form.instruction.trim(),
    gatewayId: form.gatewayId,
    inferenceQuotaId: form.inferenceQuotaId,
    connectorIds: [...form.connectorIds],
    knowledgeBaseIds: [...form.knowledgeBaseIds],
    policies: coercePolicies(form.policies),
  };
}

export const omniChatApi = {
  async list(): Promise<OmniChatAgent[]> {
    try {
      const data = (await apiService.get(`${OMNICHAT_API_BASE}/agents`, {}, OMNICHAT_CONNECTION)) as {
        items?: Record<string, unknown>[];
      };
      return asArray<Record<string, unknown>>(data?.items).map(normalizeAgent).filter((item) => item.id);
    } catch (err) {
      throw new Error(serviceErrorMessage(err));
    }
  },

  async create(form: OmniChatFormState): Promise<OmniChatAgent> {
    try {
      const data = (await apiService.post(
        `${OMNICHAT_API_BASE}/agents`,
        formPayload(form),
        OMNICHAT_CONNECTION
      )) as Record<string, unknown>;
      return normalizeAgent(data);
    } catch (err) {
      throw new Error(serviceErrorMessage(err));
    }
  },

  async update(id: string, form: OmniChatFormState): Promise<OmniChatAgent> {
    try {
      const data = (await apiService.put(
        `${OMNICHAT_API_BASE}/agents/${id}`,
        formPayload(form),
        OMNICHAT_CONNECTION
      )) as Record<string, unknown>;
      return normalizeAgent(data);
    } catch (err) {
      throw new Error(serviceErrorMessage(err));
    }
  },

  async setActive(id: string, active: boolean): Promise<OmniChatAgent> {
    try {
      const data = (await apiService.patch(
        `${OMNICHAT_API_BASE}/agents/${id}/activation`,
        { active },
        OMNICHAT_CONNECTION
      )) as Record<string, unknown>;
      return normalizeAgent(data);
    } catch (err) {
      throw new Error(serviceErrorMessage(err));
    }
  },

  async remove(id: string): Promise<void> {
    try {
      await apiService.delete(`${OMNICHAT_API_BASE}/agents/${id}`, OMNICHAT_CONNECTION);
    } catch (err) {
      throw new Error(serviceErrorMessage(err));
    }
  },

  async listAudit(agentId: string, limit = 20): Promise<OmniChatAuditEvent[]> {
    logOmniChat('app', 'audit_load', { id: agentId, limit });
    try {
      const data = (await apiService.get(
        OMNICHAT_AUDIT_BASE,
        { agentId, limit },
        OMNICHAT_CONNECTION
      )) as { items?: Record<string, unknown>[] };
      const items = asArray<Record<string, unknown>>(data?.items)
        .map(normalizeAudit)
        .filter((item) => item.eventId);
      logOmniChat('app', 'audit_load_ok', { id: agentId, count: items.length });
      return items;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logOmniChat('error', 'audit_load_failed', { message, id: agentId });
      throw new Error(message);
    }
  },
};
